import { Box, ButtonBase, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { brand, brandGradient } from "../core/Theme";
import { useAuth } from "../helpers/hooks";
import { MENU, type NavItem } from "../helpers/navigation";
import { Spacing } from "../types/enum";

/**
 * Styles
 *
 * Mirrors the member tools' launcher: a header carrying the brand strip, then
 * one card per destination grouped exactly as the sidebar groups them.
 */
const useStyles = makeStyles()({
  header: {
    position: "relative",
    background: "#fff",
    border: `1px solid ${brand.line}`,
    borderRadius: 20,
    padding: "30px 30px 26px",
    marginBottom: 30,
    boxShadow: "0 10px 30px rgba(0, 88, 61, .08)",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: "0 0 auto 0",
      height: 6,
      background: brandGradient,
    },
  },
  section: {
    margin: "24px 0",
  },
  label: {
    fontSize: "0.68rem",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: brand.grey,
    fontWeight: 600,
    marginBottom: 10,
    display: "block",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    gap: 12,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    width: "100%",
    height: "100%",
    textAlign: "left",
    padding: "15px 16px",
    border: `1px solid ${brand.line}`,
    borderRadius: 14,
    background: "#fff",
    transition: "border-color .15s, box-shadow .15s, transform .05s",
    "&:hover": {
      borderColor: brand.greenSoft,
      boxShadow: "0 4px 14px rgba(0, 0, 0, .07)",
    },
    "&:active": {
      transform: "translateY(1px)",
    },
  },
  icon: {
    color: brand.green,
    marginBottom: 2,
    "& svg": {
      width: 22,
      height: 22,
    },
  },
  title: {
    fontWeight: 600,
    color: brand.ink,
    lineHeight: 1.3,
  },
  description: {
    fontSize: "0.82rem",
    color: brand.grey,
    lineHeight: 1.35,
  },
});

const LauncherCard: React.FC<{ item: NavItem }> = ({ item }) => {
  const { classes } = useStyles();

  return (
    <ButtonBase className={classes.card} component={RouterLink} to={item.href} focusRipple>
      <Box className={classes.icon} display="flex">
        {item.icon}
      </Box>
      <Typography className={classes.title}>{item.title}</Typography>
      <Typography className={classes.description}>{item.description}</Typography>
    </ButtonBase>
  );
};

const AdminHomePage: React.FC = () => {
  const { classes } = useStyles();
  const { me } = useAuth();

  return (
    <>
      <Box className={classes.header}>
        <Typography variant="h3" color="textPrimary">
          Hi {me?.firstName ?? "there"} 💚
        </Typography>
        <Typography variant="body1" color="textSecondary" mt={Spacing.s} maxWidth="60ch">
          Everything you can manage, in one place. Pick a section below, or use the menu on the
          left.
        </Typography>
      </Box>

      {MENU.map((group) => (
        <Box key={group.title} className={classes.section}>
          <Typography component="span" className={classes.label}>
            {group.title}
          </Typography>
          <Box className={classes.grid}>
            {group.list.map((item) => (
              <LauncherCard key={item.title} item={item} />
            ))}
          </Box>
        </Box>
      ))}
    </>
  );
};

export default AdminHomePage;
